import React, { useMemo, useCallback } from 'react';
import { BookOpen } from 'lucide-react';
import BiographySection from './BiographySection';
import DynamicContent from './DynamicContent';
import { BiographyField } from './types';
import { CharacterData } from '../../types/character';

interface JournalSectionProps {
  character: CharacterData;
  updateCharacterData: (updates: any) => void;
  defaultExpanded?: boolean;
  priority?: 'high' | 'medium' | 'low';
}

/**
 * JournalSection - Specialized section for character journal entries
 * Uses BiographySection + DynamicContent composition
 */
const JournalSection: React.FC<JournalSectionProps> = ({
  character,
  updateCharacterData,
  defaultExpanded = false,
  priority = 'medium'
}) => {
  // Convert character journal data to biography fields
  const fields = useMemo((): BiographyField[] => {
    const journalEntries = character.biography?.characterJournal || [];

    return journalEntries.map((entry, index) => ({
      id: `journal-${index}`,
      label: entry.title || `Entry ${index + 1}`,
      value: entry.content || '',
      type: 'textarea' as const,
      editable: true,
      placeholder: 'Write your journal entry here...',
      maxLength: 1000,
      helpText: `Created: ${entry.date ? new Date(entry.date).toLocaleDateString() : 'Unknown'}`
    }));
  }, [character.biography?.characterJournal]);

  // Handle field changes
  const handleFieldChange = useCallback((fieldId: string, value: string | string[]) => {
    const journalEntries = [...(character.biography?.characterJournal || [])];
    const entryIndex = parseInt(fieldId.replace('journal-', ''));

    if (entryIndex >= 0 && entryIndex < journalEntries.length) {
      journalEntries[entryIndex] = {
        ...journalEntries[entryIndex],
        content: typeof value === 'string' ? value : value[0] || ''
      };

      const updates = {
        biography: {
          ...character.biography,
          characterJournal: journalEntries
        }
      };
      updateCharacterData(updates);
    }
  }, [character.biography, updateCharacterData]);

  // Handle adding new journal entry
  const handleAddField = useCallback((field: Omit<BiographyField, 'id'>) => {
    const journalEntries = [...(character.biography?.characterJournal || [])];
    const newEntry = {
      id: `entry-${Date.now()}`,
      title: field.label,
      content: '',
      date: new Date().toISOString(),
      tags: [],
      importance: 'medium' as const
    };

    journalEntries.push(newEntry);

    const updates = {
      biography: {
        ...character.biography,
        characterJournal: journalEntries
      }
    };
    updateCharacterData(updates);
  }, [character.biography, updateCharacterData]);

  // Handle removing journal entry
  const handleRemoveField = useCallback((fieldId: string) => {
    const journalEntries = [...(character.biography?.characterJournal || [])];
    const entryIndex = parseInt(fieldId.replace('journal-', ''));

    if (entryIndex >= 0 && entryIndex < journalEntries.length) {
      journalEntries.splice(entryIndex, 1);

      const updates = {
        biography: {
          ...character.biography,
          characterJournal: journalEntries
        }
      };
      updateCharacterData(updates);
    }
  }, [character.biography, updateCharacterData]);

  // Never mark as empty - DynamicContent handles empty state with "Add" button
  const isEmpty = false;

  return (
    <BiographySection
      sectionId="bio-journal"
      title="Character Journal"
      icon={<BookOpen size={18} />}
      defaultExpanded={defaultExpanded}
      priority={priority}
      isEmpty={isEmpty}
      emptyMessage="No journal entries yet"
    >
      <DynamicContent
        fields={fields}
        onFieldChange={handleFieldChange}
        onAddField={handleAddField}
        onRemoveField={handleRemoveField}
        allowCustomFields={true}
        maxFields={20}
        fieldTemplate={{
          type: 'textarea',
          editable: true,
          maxLength: 1000,
          placeholder: 'Write your journal entry here...'
        }}
        showLabels={true}
        emptyMessage="Start documenting your character's journey! Add your first journal entry to record important events, thoughts, and experiences."
        addButtonText="Add Journal Entry"
        useCard={false}
      />
    </BiographySection>
  );
};

export default JournalSection;